import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn
} from '@angular/common/http';
import {
  BehaviorSubject,
  EMPTY,
  Subject,
  catchError,
  filter,
  switchMap,
  take,
  takeUntil,
  throwError
} from 'rxjs';

const silentPollUrls = [
  '/rides/photos/statistics',
  '/damage-reports/statistics',
];
import { AuthService } from '../services/auth/auth-service';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

const RETRY_REQUEST = new HttpContextToken<boolean>(() => false);

// 🔥 Global state
let isRefreshing = false;
let isLoggedOut = false;

// 💣 cancel active requests
const cancelRequests$ = new Subject<void>();

// 🔁 queue waiting requests
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

// 🔒 request gate
const requestGate$ = new BehaviorSubject<boolean>(true);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if(req.url.endsWith('login')) isLoggedOut = false;
  
  // 🚫 block after logout
  if (isLoggedOut) {
    return throwError(() =>
      new HttpErrorResponse({ status: 401, statusText: 'Session expired' })
    );
  }

  // ⛔ skip auth endpoints
  if (req.url.includes('/auth/login') || req.url.includes('/auth/refresh')) {
    return next(req);
  }

  const accessToken = authService.getAccessToken();

  // Silent background polls: skip when token is expired instead of refreshing
  if (authService.isTokenExpired() && silentPollUrls.some(u => req.url.includes(u))) {
    return EMPTY;
  }

  if (authService.isTokenExpired() && authService.getRefreshToken()) {

    // 🔁 if refresh already running → wait
    if (isRefreshing) {
      return refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          const retryReq = req.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
          });
          return next(retryReq);
        })
      );
    }

    // 🔥 start refresh BEFORE sending any request
    isRefreshing = true;

    // 🔒 block new requests
    requestGate$.next(false);

    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap(res => {
        isRefreshing = false;

        const newToken = res?.data?.accessToken;

        if (newToken) {
          const refreshToken = authService.getRefreshToken();

          authService.setTokens(newToken, refreshToken!);

          // 🔓 open gate
          requestGate$.next(true);

          // 🔁 release waiting requests
          refreshTokenSubject.next(newToken);

          // 🔄 continue original request
          const retryReq = req.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` }
          });

          return next(retryReq);
        }

        return handleLogout(authService, router, res);
      }),

      catchError(err => handleLogout(authService, router, err))
    );
  }

  // =========================================================
  // ✅ NORMAL FLOW (token valid)
  // =========================================================

  let authReq = req;

  if (accessToken) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`
      }
    });
  }

  return requestGate$.pipe(
    filter(open => open === true),
    take(1),

    switchMap(() =>
      next(authReq).pipe(
        takeUntil(cancelRequests$),

        catchError((err: HttpErrorResponse) => {
          const isRetry = req.context.get(RETRY_REQUEST);

          // 🔥 fallback (unexpected 401)
          if (err.status === 401 && !isRetry && authService.getRefreshToken()) {

            if (!isRefreshing) {
              cancelRequests$.next(); // 💣 cancel in-flight
            }

            if (isRefreshing) {
              return refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => {
                  const retryReq = req.clone({
                    setHeaders: { Authorization: `Bearer ${token}` },
                    context: req.context.set(RETRY_REQUEST, true)
                  });
                  return next(retryReq);
                })
              );
            }

            isRefreshing = true;
            requestGate$.next(false);
            refreshTokenSubject.next(null);

            return authService.refreshToken().pipe(
              switchMap(res => {
                isRefreshing = false;

                const newToken = res?.data?.accessToken;

                if (newToken) {
                  authService.setTokens(newToken, authService.getRefreshToken()!);

                  requestGate$.next(true);
                  refreshTokenSubject.next(newToken);

                  const retryReq = req.clone({
                    setHeaders: { Authorization: `Bearer ${newToken}` },
                    context: req.context.set(RETRY_REQUEST, true)
                  });

                  return next(retryReq);
                }

                return handleLogout(authService, router, err);
              }),
              catchError(refreshErr =>
                handleLogout(authService, router, refreshErr)
              )
            );
          }

          return throwError(() => err);
        })
      )
    )
  );
};

// 🚪 logout handler
function handleLogout(
  authService: AuthService,
  router: Router,
  error: unknown
) {
  if (!isLoggedOut) {
    isLoggedOut = true;

    cancelRequests$.next();

    authService.removeTokens();

    router.navigate(['/login']);
  }

  return throwError(() => error);
}